import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

import { RegisterComponent } from '../../components/component-lookup';
import { SparklineComponent } from '../sparkline/sparkline.component';
import { Metric, MetricService } from '../../services/metric.service';
import { StateService, SubjectType } from '../../services/state.service';
import { VisualService } from '../../services/visual.service';
import { KpiComponent } from '../kpi/kpi.component';
import { ChartInfoBoxComponent } from '../chart-info-box/chart-info-box.component';

@RegisterComponent('PerformanceComponent')
@Component({
  selector: 'app-performance',
  standalone: true,
  imports: [CommonModule, SparklineComponent, KpiComponent, ChartInfoBoxComponent],
  templateUrl: './performance.component.html',
  styleUrl: './performance.component.scss'
})
export class PerformanceComponent {
  metricId = input.required<string>();
  title = input.required<string>();
  displayType = input.required<string>();
  rankSort = input<string>('N');
  tooltip = input<string>();
  percentile = input<boolean>(false);
  tourId = input<string | null>();

  private metricService = inject(MetricService);
  private visualService = inject(VisualService);

  readonly metric = signal<Metric>({} as Metric);
  readonly institutionKpiValue = signal<(number | null)>(null);
  readonly peerKpiValue = signal<(number | null)>(null);
  readonly institutionDisplayValue = computed(() => VisualService.getValueDisplayOrNull(this.institutionKpiValue(), this.displayType()));
  readonly peerDisplayValue = computed(() => VisualService.getValueDisplayOrNull(this.peerKpiValue(), this.displayType()));
  readonly sortMessage = computed(() => this.rankSort() == 'H' ? 'A higher value is better' : this.rankSort() == 'L' ? 'A lower value is better' : '&nbsp;');

  readonly institution = inject(StateService).institution;
  readonly peer = inject(StateService).peer;
  readonly dateRange = inject(StateService).dateRange;
  readonly filteredGroup = inject(StateService).filteredGroup;

  readonly SubjectType = SubjectType;

  constructor() {
    effect(() => {
      const peer = this.peer();
      const date = this.dateRange()?.endDate;
      const group = this.filteredGroup();
      if (this.institution() && this.metricId() && peer && date && group) {
        setTimeout(() => {
          this.institutionKpiValue.set(null);
          this.peerKpiValue.set(null);
        });

        this.metricService.getMetricData(this.institution().iid, this.metricId(), peer).subscribe(data => {
          const dataValues = this.visualService.getMetricValuesPeer(this.displayType(), data, date);
          setTimeout(() => {
            this.institutionKpiValue.set(dataValues.institutionValue);
            this.peerKpiValue.set(dataValues.peerValue);
          });
        });
      }
    });
  }
}
